import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import styled from 'styled-components'
import { Loading } from '../../atoms/Loading'
import { Flex } from '../../atoms/Flex'
import { Box } from '../../atoms/Box'
import {
  SeasonalPatternsChartProps,
  DEFAULT_SEASONAL_PATTERNS_CHART_LABELS,
} from './SeasonalPatternsChart.types'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
`

const ErrorContainer = styled(Flex)`
  height: 350px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LoadingContainer = styled(Flex)`
  height: 350px;
`

const ChartsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`

const FullWidthChart = styled(Box)`
  width: 100%;
  margin-top: 20px;
`

export const SeasonalPatternsChart: React.FC<SeasonalPatternsChartProps> = ({
  monthlyPatterns = [],
  quarterlyPatterns = [],
  monthAggregates = [],
  loading = false,
  error = null,
  height = 400,
  labels = {},
}) => {
  const mergedLabels = { ...DEFAULT_SEASONAL_PATTERNS_CHART_LABELS, ...labels }
  const monthlyChartRef = useRef<HTMLDivElement>(null)
  const quarterlyChartRef = useRef<HTMLDivElement>(null)
  const seasonalChartRef = useRef<HTMLDivElement>(null)
  const monthlyChartInstance = useRef<echarts.ECharts | null>(null)
  const quarterlyChartInstance = useRef<echarts.ECharts | null>(null)
  const seasonalChartInstance = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (
      !loading &&
      !error &&
      monthlyPatterns.length > 0 &&
      monthlyChartRef.current
    ) {
      initializeMonthlyChart()
    }

    return () => {
      monthlyChartInstance.current?.dispose()
    }
  }, [monthlyPatterns, loading, error])

  useEffect(() => {
    if (
      !loading &&
      !error &&
      quarterlyPatterns.length > 0 &&
      quarterlyChartRef.current
    ) {
      initializeQuarterlyChart()
    }

    return () => {
      quarterlyChartInstance.current?.dispose()
    }
  }, [quarterlyPatterns, loading, error])

  useEffect(() => {
    if (
      !loading &&
      !error &&
      monthAggregates.length > 0 &&
      seasonalChartRef.current
    ) {
      initializeSeasonalChart()
    }

    return () => {
      seasonalChartInstance.current?.dispose()
    }
  }, [monthAggregates, loading, error])

  useEffect(() => {
    const handleResize = () => {
      monthlyChartInstance.current?.resize()
      quarterlyChartInstance.current?.resize()
      seasonalChartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const formatCurrency = (value: number) => {
    if (value >= 1e9) return `CHF ${(value / 1e9).toFixed(2)}B`
    if (value >= 1e6) return `CHF ${(value / 1e6).toFixed(0)}M`
    if (value >= 1e3) return `CHF ${(value / 1e3).toFixed(0)}K`
    return `CHF ${value.toFixed(0)}`
  }

  const initializeMonthlyChart = () => {
    if (
      !monthlyChartRef.current ||
      !monthlyPatterns ||
      monthlyPatterns.length === 0
    )
      return

    if (monthlyChartInstance.current) {
      monthlyChartInstance.current.dispose()
    }

    monthlyChartInstance.current = echarts.init(monthlyChartRef.current)

    const option: echarts.EChartsOption = {
      title: {
        text: mergedLabels.monthlyActivity,
        subtext: mergedLabels.monthlyActivitySubtext,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
        subtextStyle: {
          fontSize: 12,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: (params: any) => {
          let result = `<div style="padding: 8px;"><div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`
          params.forEach((param: any) => {
            const value =
              param.seriesName === 'Transaction Count'
                ? param.value.toLocaleString()
                : formatCurrency(param.value)
            result += `<div>${param.marker} ${param.seriesName}: ${value}</div>`
          })
          result += `</div>`
          return result
        },
      },
      legend: {
        data: [mergedLabels.transactionCount!, mergedLabels.totalValue!],
        top: '12%',
        left: 'center',
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '25%',
        bottom: '15%',
      },
      xAxis: {
        type: 'category',
        data: monthlyPatterns.map((d) => d.month),
        axisLabel: {
          rotate: 45,
          fontSize: 10,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: mergedLabels.transactionCount,
          position: 'left',
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
          axisLabel: {
            formatter: (value: number) => value.toLocaleString(),
          },
        },
        {
          type: 'value',
          name: mergedLabels.totalValue,
          position: 'right',
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
          axisLabel: {
            formatter: (value: number) => formatCurrency(value),
          },
        },
      ],
      series: [
        {
          name: mergedLabels.transactionCount,
          type: 'bar',
          data: monthlyPatterns.map((d) => d.transaction_count),
          itemStyle: {
            color: '#5470c6',
          },
        },
        {
          name: mergedLabels.totalValue,
          type: 'line',
          yAxisIndex: 1,
          data: monthlyPatterns.map((d) => d.total_value),
          itemStyle: {
            color: '#91cc75',
          },
          lineStyle: {
            width: 3,
          },
          smooth: true,
        },
      ],
    }

    monthlyChartInstance.current.setOption(option)
  }

  const initializeQuarterlyChart = () => {
    if (
      !quarterlyChartRef.current ||
      !quarterlyPatterns ||
      quarterlyPatterns.length === 0
    )
      return

    if (quarterlyChartInstance.current) {
      quarterlyChartInstance.current.dispose()
    }

    quarterlyChartInstance.current = echarts.init(quarterlyChartRef.current)

    const option: echarts.EChartsOption = {
      title: {
        text: mergedLabels.quarterlyComparison,
        subtext: mergedLabels.quarterlyComparisonSubtext,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
        subtextStyle: {
          fontSize: 12,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const param = params[0]
          return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${param.axisValue}</div>
              <div>${mergedLabels.transactions}: ${param.value.toLocaleString()}</div>
            </div>
          `
        },
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '22%',
        bottom: '15%',
      },
      xAxis: {
        type: 'category',
        data: quarterlyPatterns.map((d) => d.quarter),
        axisLabel: {
          rotate: 45,
          fontSize: 10,
        },
      },
      yAxis: {
        type: 'value',
        name: mergedLabels.transactionCount,
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 600,
        },
        axisLabel: {
          formatter: (value: number) => value.toLocaleString(),
        },
      },
      series: [
        {
          type: 'bar',
          data: quarterlyPatterns.map((d) => d.transaction_count),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#fac858' },
              { offset: 1, color: '#ee6666' },
            ]),
            borderRadius: [4, 4, 0, 0],
          },
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => params.value.toLocaleString(),
            fontSize: 10,
          },
        },
      ],
    }

    quarterlyChartInstance.current.setOption(option)
  }

  const initializeSeasonalChart = () => {
    if (
      !seasonalChartRef.current ||
      !monthAggregates ||
      monthAggregates.length === 0
    )
      return

    if (seasonalChartInstance.current) {
      seasonalChartInstance.current.dispose()
    }

    seasonalChartInstance.current = echarts.init(seasonalChartRef.current)

    const option: echarts.EChartsOption = {
      title: {
        text: mergedLabels.seasonalPatterns,
        subtext: mergedLabels.seasonalPatternsSubtext,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
        subtextStyle: {
          fontSize: 12,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const param = params[0]
          return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${param.axisValue}</div>
              <div>Average Transactions: ${Math.round(param.value).toLocaleString()}</div>
            </div>
          `
        },
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '22%',
        bottom: '15%',
      },
      xAxis: {
        type: 'category',
        data: monthAggregates.map((d) => d.month_name),
        name: mergedLabels.month,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 600,
        },
      },
      yAxis: {
        type: 'value',
        name: mergedLabels.averageTransactionCount,
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 600,
        },
        axisLabel: {
          formatter: (value: number) => value.toLocaleString(),
        },
      },
      series: [
        {
          type: 'bar',
          data: monthAggregates.map((d) => d.avg_count),
          itemStyle: {
            color: (params: any) => {
              // Color by season: winter (blue), spring (green), summer (yellow), autumn (orange)
              const monthIndex = params.dataIndex
              if (monthIndex < 2 || monthIndex === 11) return '#5470c6' // Winter: Dec, Jan, Feb
              if (monthIndex < 5) return '#91cc75' // Spring: Mar, Apr, May
              if (monthIndex < 8) return '#fac858' // Summer: Jun, Jul, Aug
              return '#ee6666' // Autumn: Sep, Oct, Nov
            },
          },
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) =>
              Math.round(params.value).toLocaleString(),
            fontSize: 10,
          },
        },
      ],
    }

    seasonalChartInstance.current.setOption(option)
  }

  if (loading) {
    return (
      <LoadingContainer justify="center" align="center">
        <Loading size="large" />
      </LoadingContainer>
    )
  }

  if (error) {
    return (
      <ErrorContainer justify="center" align="center">
        {error}
      </ErrorContainer>
    )
  }

  if (
    monthlyPatterns.length === 0 &&
    quarterlyPatterns.length === 0 &&
    monthAggregates.length === 0
  ) {
    return (
      <ErrorContainer justify="center" align="center">
        {mergedLabels.noData}
      </ErrorContainer>
    )
  }

  return (
    <ChartContainer>
      <ChartsGrid>
        {monthlyPatterns.length > 0 && (
          <ChartWrapper $height={height} ref={monthlyChartRef} />
        )}
        {quarterlyPatterns.length > 0 && (
          <ChartWrapper $height={height} ref={quarterlyChartRef} />
        )}
      </ChartsGrid>
      {monthAggregates.length > 0 && (
        <FullWidthChart>
          <ChartWrapper $height={height} ref={seasonalChartRef} />
        </FullWidthChart>
      )}
    </ChartContainer>
  )
}
